import { useEffect, useRef, useState } from 'react';
import type { ClientSession, ServerMessage } from '../types';

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

function socketUrl(session: ClientSession): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({
    player_id: session.player_id,
    session_id: session.session_id,
  });
  return `${protocol}//${window.location.host}/api/games/${session.game_id}/ws?${params}`;
}

export function useWebSocket(
  session: ClientSession | null,
  onMessage: (message: ServerMessage) => void
) {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [error, setError] = useState<string>('');
  const socketRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!session) return;
    setStatus('connecting');
    const ws = new WebSocket(socketUrl(session));
    socketRef.current = ws;

    ws.onopen = () => setStatus('open');
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => {
      setError('Connection failed');
      setStatus('error');
    };
    ws.onmessage = (event) => {
      try {
        onMessageRef.current(JSON.parse(event.data as string) as ServerMessage);
      } catch {
        setError('Received an invalid server message');
      }
    };

    const heartbeat = window.setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'heartbeat', timestamp: new Date().toISOString() }));
      }
    }, 15000);

    const onVisibility = () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({
          type: 'player_status',
          status: document.hidden ? 'away' : 'active',
          timestamp: new Date().toISOString(),
        })
      );
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      ws.close(1000, 'leaving page');
    };
  }, [session]);

  const send = (payload: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError('Connection is not open');
      return false;
    }
    socket.send(JSON.stringify({ ...payload, timestamp: new Date().toISOString() }));
    return true;
  };

  return { status, error, send };
}
